import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {Card} from "../../../basic/card.tsx";
import {Button} from "../../../basic/button.tsx";
import {Plus} from "lucide-react";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {LicenseStats} from "./LicenseStats.tsx";
import {LicenseTabs} from "./LicenseTabs.tsx";
import {useGetPortalLicensesQuery} from "../../../../store/generated/api.ts";
import {PaginationControls} from "../../../shared/Pagination.tsx";
import {SearchInput} from "../../../shared/search/SearchInput.tsx";

export function LicensesView() {
    const {t} = useTranslationWithKeys();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    // API call for filtered license list (with search and pagination)
    const {
        data: licensesData,
        isLoading,
        error,
        refetch
    } = useGetPortalLicensesQuery({
        page: currentPage,
        size: pageSize,
        search: searchTerm || undefined
    });

    // Separate API call for global stats (without search, uses cached results)
    const {data: globalStatsData} = useGetPortalLicensesQuery({});

    const licenses = licensesData?.licenses || [];
    const pagination = licensesData?.pagination;

    // Use global stats from separate API call (unfiltered)
    const licenseSummary = {
        totalLicenses: globalStatsData?.pagination?.total || 0,
        totalAvailableSeats: globalStatsData?.totalAvailableSeats || 0,
        totalAssignedSeats: globalStatsData?.totalAssignedSeats || 0,
        expiringSoon: globalStatsData?.expiringSoon || 0,
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page - 1);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setCurrentPage(0);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setCurrentPage(0);
    };

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1>{t('licenses.title')}</h1>
                        <p className="text-muted-foreground mt-1">{t('licenses.subtitle')}</p>
                    </div>
                    <Button onClick={() => navigate("/pricing")}>
                        <Plus className="h-4 w-4 mr-2"/>
                        {t('licenses.purchaseLicenses')}
                    </Button>
                </div>
                <div className="animate-pulse">
                    <div className="h-32 bg-gray-200 rounded-lg mb-6"></div>
                    <div className="h-64 bg-gray-200 rounded-lg"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1>{t('licenses.title')}</h1>
                        <p className="text-muted-foreground mt-1">{t('licenses.subtitle')}</p>
                    </div>
                    <Button onClick={() => navigate("/pricing")}>
                        <Plus className="h-4 w-4 mr-2"/>
                        {t('licenses.purchaseLicenses')}
                    </Button>
                </div>
                <Card className="p-6 shadow-sm border-border/50">
                    <div className="text-center py-8">
                        <p className="text-red-600 mb-4">{t('common.errorLoadingData')}</p>
                        <Button onClick={() => refetch()} variant="outline">
                            {t('common.retry')}
                        </Button>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1>{t('licenses.title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('licenses.subtitle')}</p>
                </div>
                <Button onClick={() => navigate("/pricing")}>
                    <Plus className="h-4 w-4 mr-2"/>
                    {t('licenses.purchaseLicenses')}
                </Button>
            </div>

            <LicenseStats licenses={licenses} summary={licenseSummary}/>

            <Card className="shadow-sm border-border/50">
                <div className="p-6">
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <SearchInput
                                value={searchTerm}
                                onChange={handleSearchChange}
                                placeholder={t('licenses.searchPlaceholder')}
                                className="max-w-sm"
                            />
                        </div>

                        <LicenseTabs licenses={licenses}/>

                        {pagination && (
                            <PaginationControls
                                currentPage={currentPage + 1}
                                totalPages={pagination.totalPages}
                                pageSize={pageSize}
                                pageSizeOptions={[10, 25, 50, 100]}
                                paginationInfo={{
                                    start: currentPage * pageSize + 1,
                                    end: Math.min((currentPage + 1) * pageSize, pagination.total),
                                    total: pagination.total
                                }}
                                onPageChange={handlePageChange}
                                onPageSizeChange={handlePageSizeChange}
                            />
                        )}
                    </div>
                </div>
            </Card>
        </div>
    );
}